import React, { useState } from 'react';
import { Printer, CheckCircle, X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateJob, refreshJobs, refreshArchiveJobs } from '../../redux/features/jobSlice';
import { formatDisplayValue } from '../../utils/displayUtils';
import {
  computeHandoffSettlement,
  formatRsd,
  isHandoffSettlementVisible,
  resolveHandoffPartnerRecord
} from '../../utils/handoffSettlementUtils';

const HandoffSettlementActions = ({ job, currentCompanyId, partners = [], onArchived }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  if (!isHandoffSettlementVisible(job, currentCompanyId)) return null;
  if (job.handoffSenderArchived) {
    return (
      <p className="text-[10px] text-slate-400 pt-2 border-t border-gray-600">
        Posao je arhiviran i dostupan u Arhivi i Analitici.
      </p>
    );
  }

  const partnerRecord = resolveHandoffPartnerRecord(job, partners, currentCompanyId);
  const commissionRate = partnerRecord?.commissionRate ?? 0;
  const settlement = computeHandoffSettlement(job, commissionRate);
  const partnerName =
    partnerRecord?.companyName || partnerRecord?.name || 'Servisni partner';

  const handleArchive = async () => {
    try {
      setLoading(true);
      await dispatch(
        updateJob({
          id: job._id,
          jobData: {
            status: 'Archived',
            handoffPartnerClosed: true,
            handoffWorkflowPhase: 'closed',
            handoffSenderArchived: true
          }
        })
      ).unwrap();
      toast.success('Posao je arhiviran i vidljiv u Arhivi za današnji datum');
      dispatch(refreshArchiveJobs());
      if (onArchived) onArchived(job._id);
    } catch (error) {
      toast.error(error?.message || 'Greška pri arhiviranju posla');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mt-2 pt-2 border-t border-gray-600 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => setShowPrintModal(true)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-500 text-[10px] sm:text-xs"
        >
          <Printer className="h-3.5 w-3.5" />
          Radni nalog / Štampa
        </button>
        <button
          type="button"
          onClick={handleArchive}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-[10px] sm:text-xs"
        >
          <CheckCircle className="h-3.5 w-3.5" />
          {loading ? 'Arhivira se...' : 'Arhiviraj'}
        </button>
      </div>

      {showPrintModal && (
        <div className="print-modal-overlay fixed inset-0 z-50 p-4 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm">
          <div className="print-modal-shell w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border border-slate-700/70 bg-slate-900/95 p-4 sm:p-6 shadow-2xl">
            <div className="print-ui-only flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Obračun prosleđenog posla</h3>
                <p className="text-xs text-gray-400 mt-1">Partner: {partnerName}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPrintModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="print-preview-content mx-auto w-full max-w-[720px] bg-white text-slate-800 rounded-xl p-5 sm:p-6 mb-4 border border-slate-200">
              <h2 className="text-lg font-bold mb-3">Radni nalog — obračun</h2>

              <div className="space-y-2 text-sm">
                <p>
                  <strong>Klijent:</strong> {formatDisplayValue(job.clientName)}
                </p>
                <p>
                  <strong>Adresa:</strong> {formatDisplayValue(job.clientAddress)}
                </p>
                <p>
                  <strong>Uređaj:</strong>{' '}
                  {[job.deviceType, job.deviceBrand, job.deviceModel].filter(Boolean).join(' ') || '-'}
                </p>
                {job.issueDescription && (
                  <p>
                    <strong>Prijavljeni kvar:</strong> {job.issueDescription}
                  </p>
                )}
                {job.report?.workDescription && (
                  <p>
                    <strong>Šta je popravljeno:</strong> {job.report.workDescription}
                  </p>
                )}
                {job.report?.materialsUsed && (
                  <p>
                    <strong>Korišćeni materijali:</strong> {job.report.materialsUsed}
                  </p>
                )}
                {job.report?.additionalNotes && (
                  <p>
                    <strong>Napomene:</strong> {job.report.additionalNotes}
                  </p>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-200 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Naplaćeno</span>
                  <span>{formatRsd(settlement.serviceCharged)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Troškovi</span>
                  <span>{formatRsd(settlement.totalCost)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Čista zarada</span>
                  <span>{formatRsd(settlement.profit)}</span>
                </div>
                <div className="flex justify-between text-blue-700">
                  <span>Udeo partnera ({settlement.commissionRate}%)</span>
                  <span>{formatRsd(settlement.partnerShare)}</span>
                </div>
                <div className="flex justify-between text-emerald-700 font-semibold">
                  <span>Udeo centralnog servisa</span>
                  <span>{formatRsd(settlement.centralShare)}</span>
                </div>
                {job.report?.fiscalReceiptNumber && (
                  <div className="flex justify-between pt-2 border-t border-slate-100">
                    <span>Fiskalni račun</span>
                    <span>{job.report.fiscalReceiptNumber}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="print-ui-only flex gap-2">
              <button
                type="button"
                onClick={() => {
                  window.print();
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                <Printer className="h-4 w-4" />
                Štampaj
              </button>
              <button
                type="button"
                onClick={() => setShowPrintModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 text-sm"
              >
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HandoffSettlementActions;
